/**
 * Which mark a PREDEFINED question wears — one glyph per standard catalog CODE.
 *
 * Owner, 17 September 2026: the editor's standard rows are a column of words, and „Sen", „Energia"
 * and „Waga" are read one after another when the shape of each is the fact the trainer is scanning
 * for. So each code gets a minimalist grey mark before its label. No colour: the accent on this
 * screen is spent on the weekday chip, the enabled switches and the mapping arrow, and eighteen
 * orange glyphs in one column is the accent-as-background failure the canon's own rule names.
 *
 * ── The mapping keys on the CODE, never on the answer type ──────────────────────────────────
 * Four of the catalog's questions are `Number` and three are `LongText`; a type-keyed map would draw
 * the same mark on „Wzrost", „Waga startowa" and „Waga", which is precisely the distinction the
 * glyph exists to make. The answer-type glyph is the FALLBACK, for a code this build has never heard
 * of — the catalog is the backend's and it grows without this app being rebuilt.
 *
 * ── A trainer's own question gets NO glyph ──────────────────────────────────────────────────
 * `null` for an absent code. A custom question has no shape to promise: it is whatever the trainer
 * wrote, and its answer type is already said in words on the pill beside it. Drawing a mark there
 * would put one on every row of „Pytania dodatkowe" and make the standard sections' marks mean
 * nothing.
 *
 * ── A question that reports a VITAL wears the vital's mark ───────────────────────────────────
 * Where the wire names the reading a question's answer is (`vitalType`: hours slept, energy), that
 * decides before the code does — it is the server saying what the answer is, and it holds for a
 * catalog question this build has never heard of. A question with no `vitalType` — everything else
 * in the catalog, and every snapshot frozen before the field existed — is read by its code.
 *
 * ── Two codes share one glyph, deliberately ─────────────────────────────────────────────────
 * `initial_weight` and `checkin_weight` are both a body weight on a scale — one asked once, one
 * asked every cycle — and a second drawing of a weighing scale would say they are different kinds
 * of fact. `dob` takes a cake rather than a calendar so that `availability`, which is genuinely
 * about days, can have the calendar.
 *
 * The codes are `StandardQuestionCatalog.cs`'s. No React, no DOM, no query hook, no i18next.
 */

/**
 * The marks this screen draws. A KEY rather than a component, so the decision stays testable and
 * `ui/` owns the drawing — `model/` may not import a `.tsx`.
 */
export type QuestionGlyphKey =
  | 'cake'
  | 'person'
  | 'height'
  | 'weight'
  | 'bandage'
  | 'heart'
  | 'pill'
  | 'activity'
  | 'target'
  | 'medal'
  | 'calendar'
  | 'dumbbell'
  | 'utensils'
  | 'moon'
  | 'bolt'
  | 'ruler'
  | 'camera'
  /* The FALLBACK marks, and every one of them is a glyph `ui/AnswerTypePill` already draws for that
   * answer type. An unrecognised code therefore wears exactly the mark its own type pill wears,
   * rather than a second drawing invented for this column. */
  | 'shortText'
  | 'longText'
  | 'singleChoice'
  | 'multiChoice'
  | 'number'
  | 'scale'
  | 'clip';

/**
 * Code → mark, for every question `StandardQuestionCatalog` can hand over.
 *
 * A plain record rather than a `Record<StandardCode, …>`: the codes are the BACKEND's, and a union
 * type here would be a second declaration of the catalog that goes stale silently the day a question
 * is added. `questionGlyphs.test.ts` is what pins the coverage instead.
 */
const BY_CODE: Readonly<Record<string, QuestionGlyphKey>> = {
  // Personal — the four locked, mapped intake questions (R2).
  dob: 'cake',
  gender: 'person',
  height: 'height',
  initial_weight: 'weight',

  // Health — the three art. 9 questions plus the lifestyle one that is not (R7).
  injuries: 'bandage',
  conditions: 'heart',
  medications: 'pill',
  activity_level: 'activity',

  // Training and diet (R8, R9).
  goal: 'target',
  experience: 'medal',
  availability: 'calendar',
  equipment: 'dumbbell',
  diet: 'utensils',

  // Wellbeing — the recurring check-in questions (R20, R27, R29).
  checkin_sleep: 'moon',
  // Hours slept (R37) is the same night as the sleep rating, asked as a quantity: one moon, as the
  // two body weights share one scale.
  checkin_sleep_hours: 'moon',
  checkin_energy: 'bolt',
  checkin_weight: 'weight',
  checkin_measurements: 'ruler',
  checkin_photos: 'camera',
};

/** The reading a question reports → its mark: the moon the sleep rating wears, energy's bolt. */
const BY_VITAL: Readonly<Record<string, QuestionGlyphKey>> = {
  SleepDuration: 'moon',
  EnergyLevel: 'bolt',
};

/**
 * The FALLBACK: a code this build does not know, named by its ANSWER TYPE instead.
 *
 * The same eleven types and the same eleven marks `ui/AnswerTypePill` draws, so a standard question
 * this build has never heard of wears the mark its own pill wears. `answerTypeIcon`'s own default is
 * short text, and so is this one.
 */
const BY_ANSWER_TYPE: Readonly<Record<string, QuestionGlyphKey>> = {
  ShortText: 'shortText',
  LongText: 'longText',
  SingleChoice: 'singleChoice',
  MultiChoice: 'multiChoice',
  Number: 'number',
  Scale: 'scale',
  Date: 'calendar',
  ExerciseClip: 'clip',
  Photo: 'camera',
  GoalBuilder: 'target',
  Measurements: 'ruler',
};

/**
 * The mark a question row draws before its label, or `null` for one the trainer wrote themselves.
 *
 * `vitalType` decides where the question carries one; `answerType` is read only when the code is
 * unrecognised — see the header.
 */
export function questionGlyph(
  code: string | null | undefined,
  answerType: string | null | undefined,
  vitalType?: string | null,
): QuestionGlyphKey | null {
  if (code == null || code === '') return null;
  return BY_VITAL[vitalType ?? ''] ?? BY_CODE[code] ?? BY_ANSWER_TYPE[answerType ?? ''] ?? 'shortText';
}
